import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { McIcon } from "@maersk-global/mds-react-wrapper/components-core/mc-icon";
import "./RowActionsMenu.css";

/**
 * Compact "Actions" trigger for a table row — a single icon opens a small
 * popover listing the row's actions (Report / Upload Artifacts / Edit).
 * The popover is portaled to <body> and fixed-positioned so it is never
 * clipped by the table's own scroll/overflow container.
 */
function RowActionsMenu({ items }) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, right: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const visibleItems = items.filter((item) => item && !item.hidden);

  const openMenu = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setCoords({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setIsOpen(true);
  }, []);

  const closeMenu = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        closeMenu();
      }
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") closeMenu();
    };
    const handleReposition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (rect) setCoords({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [isOpen, closeMenu]);

  if (visibleItems.length === 0) return null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="row-actions-trigger"
        title="Actions"
        aria-label="Row actions"
        onClick={() => (isOpen ? closeMenu() : openMenu())}
      >
        <McIcon icon="ellipsis-vertical" size="16" />
      </button>

      {isOpen && ReactDOM.createPortal(
        <div
          ref={menuRef}
          className="row-actions-menu"
          style={{ top: coords.top, right: coords.right }}
        >
          {visibleItems.map((item, idx) => (
            item.render ? (
              <React.Fragment key={idx}>{item.render(closeMenu)}</React.Fragment>
            ) : (
              <button
                key={idx}
                type="button"
                className="row-actions-menu-item"
                disabled={item.disabled}
                title={item.disabled ? item.disabledTitle : undefined}
                onClick={() => {
                  closeMenu();
                  item.onClick?.();
                }}
              >
                <McIcon icon={item.icon} size="16" />
                <span>{item.label}</span>
              </button>
            )
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

export default RowActionsMenu;
